import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';

export interface UseOverflowFiltersOptions {
    threshold?: number;
    debounceTime?: number;
}

export interface UseOverflowFiltersReturn {
    containerRef: React.RefObject<HTMLDivElement>;
    hasOverflow: boolean;
    showAllFilters: boolean;
    hiddenCount: number;
    visibleCount: number;
    toggleShowMore: () => void;
    isInitialized: boolean;
}

const DEFAULT_VISIBLE_COUNT = 10;

export const useOverflowFilters = (options: UseOverflowFiltersOptions = {}): UseOverflowFiltersReturn => {
    const { threshold = 20, debounceTime = 150 } = options;

    const containerRef = useRef<HTMLDivElement>(null);
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [overflowData, setOverflowData] = useState({
        hasOverflow: false,
        hiddenCount: 0,
        visibleCount: DEFAULT_VISIBLE_COUNT,
        isInitialized: false,
    });

    const debounceTimeoutRef = useRef<NodeJS.Timeout>();
    const isCalculatingRef = useRef(false);

    const calculateOverflowData = useCallback(() => {
        if (!containerRef.current || isCalculatingRef.current) {
            return null;
        }

        const container = containerRef.current;
        const children = Array.from(container.children).filter((child) => {
            const element = child as HTMLElement;
            return element.tagName && !('showMoreButton' in element.dataset);
        }) as HTMLElement[];

        if (children.length === 0) {
            return {
                hasOverflow: false,
                hiddenCount: 0,
                visibleCount: 0,
                isInitialized: true,
            };
        }

        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        let currentWidth = 0;
        let visibleCountCalculated = 0;

        const computedContainerStyle = getComputedStyle(container);
        const gap = Number.parseFloat(computedContainerStyle.columnGap || computedContainerStyle.gap || '0');

        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const originalDisplay = child.style.display;
            child.style.display = '';

            const rect = child.getBoundingClientRect();
            const computedStyle = getComputedStyle(child);
            const marginLeft = Number.parseFloat(computedStyle.marginLeft) || 0;
            const marginRight = Number.parseFloat(computedStyle.marginRight) || 0;

            const totalWidth = rect.width + marginLeft + marginRight + (i > 0 ? gap : 0);
            child.style.display = originalDisplay;

            if (currentWidth + totalWidth + threshold <= containerWidth) {
                currentWidth += totalWidth;
                visibleCountCalculated++;
            } else {
                break;
            }
        }

        const totalChildren = children.length;
        const hasOverflowDetected = visibleCountCalculated < totalChildren;
        const hiddenCountCalculated = totalChildren - visibleCountCalculated;

        return {
            hasOverflow: hasOverflowDetected,
            hiddenCount: hiddenCountCalculated,
            visibleCount: visibleCountCalculated,
            isInitialized: true,
        };
    }, [threshold]);

    const applyVisibilityStyles = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const children = Array.from(container.children).filter((child) => {
            const element = child as HTMLElement;
            return element.tagName && !('showMoreButton' in element.dataset);
        }) as HTMLElement[];

        isCalculatingRef.current = true;

        children.forEach((child, index) => {
            if (showAllFilters || index < overflowData.visibleCount) {
                child.style.display = '';
            } else {
                child.style.display = 'none';
            }
        });

        setTimeout(() => {
            isCalculatingRef.current = false;
        }, 10);
    }, [showAllFilters, overflowData.visibleCount]);

    const debouncedCalculate = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                const newData = calculateOverflowData();
                if (newData) {
                    setOverflowData(newData);
                }
            });
        }, debounceTime);
    }, [calculateOverflowData, debounceTime]);

    const toggleShowMore = useCallback(() => {
        setShowAllFilters((prev) => !prev);
        setTimeout(() => {
            debouncedCalculate();
        }, 50);
    }, [debouncedCalculate]);

    useEffect(() => {
        if (overflowData.isInitialized) {
            applyVisibilityStyles();
        }
    }, [showAllFilters, applyVisibilityStyles, overflowData.isInitialized]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const handleInitialize = () => {
            const initialData = calculateOverflowData();
            if (initialData) {
                setOverflowData(initialData);
            }
        };

        const initialize = () => {
            requestAnimationFrame(handleInitialize);
        };

        if (container.getBoundingClientRect().width > 0) {
            initialize();
        } else {
            const observer = new ResizeObserver(() => {
                if (container.getBoundingClientRect().width > 0) {
                    initialize();
                    observer.disconnect();
                }
            });
            observer.observe(container);
        }

        const resizeObserver = new ResizeObserver(() => {
            debouncedCalculate();
        });
        resizeObserver.observe(container);

        // @one dropdowns settle their width async without changing the container, so observe
        // the items too and recalc once their real width is known (calc is idempotent).
        const observeFilterItems = () => {
            for (const child of Array.from(container.children)) {
                resizeObserver.observe(child);
            }
        };
        observeFilterItems();

        const mutationObserver = new MutationObserver((mutations) => {
            const hasChildListChanges = mutations.some(
                (mutation) =>
                    mutation.type === 'childList' &&
                    (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
            );
            if (hasChildListChanges && !isCalculatingRef.current) {
                for (const mutation of mutations) {
                    for (const node of Array.from(mutation.removedNodes)) {
                        if (node instanceof Element) {
                            resizeObserver.unobserve(node);
                        }
                    }
                }
                observeFilterItems();
                debouncedCalculate();
            }
        });

        mutationObserver.observe(container, { childList: true, subtree: false });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [calculateOverflowData, debouncedCalculate]);

    useEffect(() => {
        if (!overflowData.hasOverflow && showAllFilters) {
            setShowAllFilters(false);
        }
    }, [overflowData.hasOverflow, showAllFilters]);

    return useMemo(
        () => ({
            containerRef,
            hasOverflow: overflowData.hasOverflow,
            showAllFilters,
            hiddenCount: overflowData.hiddenCount,
            visibleCount: overflowData.visibleCount,
            toggleShowMore,
            isInitialized: overflowData.isInitialized,
        }),
        [
            overflowData.hasOverflow,
            overflowData.hiddenCount,
            overflowData.visibleCount,
            overflowData.isInitialized,
            showAllFilters,
            toggleShowMore,
        ]
    );
};
